import React, { useEffect, useMemo, useState } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Container } from "../../globalStyles";
import { useClipsActions, useClipsState, useContributorsState } from "@/store";
import { GridContainer } from "./Clip.elements";
import ClipCard from "./ClipCard";
import Pagination from "../Util/Pagination";
import SearchPanel from "../Util/SearchPanel";
import DataLoader from "../Loader/DataLoader/DataLoader";
import NoDataState from "@/components/EmptyData/NoDataState";
import { StatsSection, StatsTopBar, StatsTitleLeft, StatsDivider } from "../Stats/Counter/CountersBlock.elements";

const ClipsGroup = () => {
  const { selectedContributorId, affiliate } = useContributorsState();
  const { clips, totalPages, currentPage, totalElements, status } = useClipsState();
  const { fetchClips, setPage } = useClipsActions();

  const [searchParams, setSearchParams] = useState({
    region: "",
    searchTerm: "",
    clipTypes: [],
    startDate: undefined,
    endDate: undefined,
  });

  const [pageSize, setPageSize] = useState(10);

  const isLoading = status === "loading";
  const hasData = (clips?.length ?? 0) > 0;
  const totalClips = totalElements ?? 0;

  useEffect(() => {
    const { region, clipTypes, searchTerm, startDate, endDate } = searchParams;

    fetchClips({
      page: currentPage,
      pageSize,
      contributors: selectedContributorId,
      affiliate,
      clipTypes,
      region,
      searchTerm,
      fromDate: startDate,
      toDate: endDate,
    });
  }, [fetchClips, currentPage, pageSize, affiliate, selectedContributorId, searchParams]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) setPage(pageNumber);
  };

  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
  };

  const onSetSearchParams = (next) => {
    setSearchParams(next);
    setPage(0);
  };

  const centeredWrapStyle = useMemo(
      () => ({ display: "flex", justifyContent: "center", padding: "40px 0" }),
      []
  );

  return (
      <Container>
        <StatsSection>
          <StatsTopBar>
            <StatsTitleLeft>Clips</StatsTitleLeft>
          </StatsTopBar>

          <SearchPanel
              setSearchParams={onSetSearchParams}
              hasRegionField
              hasClipTypeFilters
              searchLabel="Search Clip"
              searchPlaceholder="Enter clip ID, upload ID, filename, headline, or description"
          />

          <StatsDivider />

          {!isLoading && hasData && (
              <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    px: { xs: 0, sm: 2 },
                    pt: 2,
                    pb: 2,
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
              >
                <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.5,
                      py: 1,
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                  <Typography
                      sx={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                  >
                    Total clips
                  </Typography>
                  <Typography
                      sx={{
                        color: "#fff",
                        fontSize: "1rem",
                        fontWeight: 800,
                        lineHeight: 1,
                      }}
                  >
                    {totalClips}
                  </Typography>
                </Box>
              </Stack>
          )}

          {isLoading ? (
              <div style={centeredWrapStyle}>
                <DataLoader />
              </div>
          ) : !hasData ? (
              <div style={centeredWrapStyle}>
                <NoDataState />
              </div>
          ) : (
              <>
                <GridContainer>
                  {clips.map((file) => (
                      <ClipCard key={file.fileName} file={file} />
                  ))}
                </GridContainer>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
              </>
          )}
        </StatsSection>
      </Container>
  );
};

export default ClipsGroup;